import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import {
  MlsListingObject,
  PartyFragment,
} from '@client/graphql/__generated__/types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAddBuyerForm } from '../../Buyer/AddBuyerForm/hooks/useAddBuyerForm';
import { AddOrEditBuyer } from '../../Buyer/AddOrEditBuyer';
import { MLSListingRow } from '../../Listings/MLSListingRow';
import { FindPropertyPopoverList } from '../../MakeOffer/FindPropertyPopoverList';
import { useCreateBuyerOfferAnalysis } from '../hooks/useCreateBuyerOfferAnalysis';

interface AddBuyerOfferAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  partyId?: string;
  mlsListing?: MlsListingObject;
}

export const AddBuyerOfferAnalysisModal = ({
  isOpen,
  onClose,
  partyId,
  mlsListing,
}: AddBuyerOfferAnalysisModalProps) => {
  const navigate = useNavigate();
  const [selectedMlsListing, setSelectedMlsListing] = useState<
    MlsListingObject | undefined
  >(mlsListing || undefined);
  const [selectedParty, setSelectedParty] = useState<PartyFragment | undefined>(
    undefined
  );
  const [isEditing, setIsEditing] = useState(false);
  const [showAddBuyerForm, setShowAddBuyerForm] = useState(false);
  const { buyerParties } = useAddBuyerForm({});
  const { handleCreate, loading } = useCreateBuyerOfferAnalysis();

  const onSubmit = async () => {
    if (!selectedMlsListing) {
      return;
    }

    const buyerOfferAnalysis = await handleCreate({
      mlsListingId: selectedMlsListing?.id,
      partyId: selectedParty?.id,
    });

    if (!buyerOfferAnalysis?.id) {
      return;
    }

    navigate(`/offer-scorecard/${buyerOfferAnalysis.id}`);
  };

  useEffect(() => {
    if (partyId) {
      setSelectedParty(buyerParties?.find((p) => p.id === partyId));
    }
  }, [partyId, buyerParties]);

  useEffect(() => {
    if (mlsListing) {
      setSelectedMlsListing(mlsListing);
    }
  }, [mlsListing]);

  return (
    <Modal
      isOpen={isOpen}
      scrollBehavior={showAddBuyerForm ? 'inside' : undefined}
      size="lg"
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Offer Scorecard</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack alignItems="stretch" flexGrow={1} spacing={4}>
            <FormControl>
              <FormLabel>Select a Property</FormLabel>
              <FindPropertyPopoverList
                closeMenuOnSelect
                size="sm"
                onSelect={(mlsListing) => {
                  setSelectedMlsListing(mlsListing);
                }}
              />
            </FormControl>
            {selectedMlsListing && (
              <MLSListingRow
                key={selectedMlsListing.id}
                listing={selectedMlsListing}
                onRemove={() => {
                  setSelectedMlsListing(undefined);
                }}
              />
            )}

            <FormControl>
              <FormLabel>Select a Buyer</FormLabel>
              <AddOrEditBuyer
                isEditing={isEditing}
                selectedParty={selectedParty}
                setIsEditing={setIsEditing}
                setSelectedParty={setSelectedParty}
                setShowAddBuyerForm={setShowAddBuyerForm}
                showAddBuyerForm={showAddBuyerForm}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter px={8}>
          <Button
            isDisabled={!selectedMlsListing || isEditing}
            isLoading={loading}
            width="100%"
            onClick={() => void onSubmit()}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
